import classNames from 'classnames';
import qs from 'qs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '../../constants';
import { useMobile } from '../../hooks/useMobile';
import Icon from '../../icons';
import { CustomPaginationProps } from '../../interfaces';
import CustomSelect from '../Select';
import {
  AmiliArrowButton,
  AmiliPageWrapper,
  AmiliPagesWrapper,
  AmiliPaginationWrapper,
  CustomTotal,
  SizeChangerWrapper,
} from './styles';

const DEFAULT_PAGE_RANGE_SIZE = 10;
const MOBILE_PAGE_RANGE_SIZE = 5;

const AmiliPagination: React.FC<CustomPaginationProps> = props => {
  const {
    onChange,
    total = 0,
    defaultPageSize,
    showSizeChanger = true,
    pageSizeOptions = PAGE_SIZE_OPTIONS,
    subTotalValue, // for case that need render another "total" at the same time with total item
    stateParams, //for case that using state instead of qs params
  } = props;

  const { t } = useTranslation();
  const { search } = useLocation();
  const qsParams = useMemo(
    () => qs.parse(search, { ignoreQueryPrefix: true }),
    [search],
  );

  const isMobile = useMobile();

  const pageRangeSize = isMobile
    ? MOBILE_PAGE_RANGE_SIZE
    : DEFAULT_PAGE_RANGE_SIZE;

  const qsParamsPage = stateParams?.page ? stateParams?.page : qsParams.page;
  const qsParamsTake = stateParams?.take ? stateParams?.take : qsParams.take;
  const [page, setPage] = useState(Number(qsParamsPage) || 1);
  const [pageSize, setPageSize] = useState(
    defaultPageSize || Number(qsParamsTake) || DEFAULT_PAGE_SIZE,
  );

  const selectOptions = useMemo(() => {
    return pageSizeOptions.map(option => ({
      label: t('design.pageSize', { QUANTITY: option }),
      value: option,
    }));
  }, [pageSizeOptions, t]);

  const handleChange = useCallback(
    (page: number, pageSize: number) => {
      setPage(page);
      setPageSize(pageSize);
      onChange?.(page, pageSize);
    },
    [setPage, setPageSize, onChange],
  );

  const pages = Math.ceil(total / pageSize);

  // UPDATED: Replaced hardcoded '10' and '9' with `pageRangeSize`
  const rangePage = useMemo(() => {
    const max = Math.ceil(page / pageRangeSize) * pageRangeSize;
    // `max - 9` becomes `max - (pageRangeSize - 1)`, which simplifies to `max - pageRangeSize + 1`
    const from = Math.max(1, max - pageRangeSize + 1);
    const maxOfPages = Math.min(max, pages);

    return { from, to: maxOfPages };
  }, [page, pages, pageRangeSize]);

  // UPDATED: Logic for handlePrev is now dynamic and more intuitive
  const handlePrev = () => {
    // Navigate to the last page of the previous block
    const value = rangePage.from - 1;
    if (value >= 1) {
      handleChange(value, pageSize);
    }
  };

  const handleNext = () => {
    const value = rangePage.to + 1;
    if (value <= pages) {
      handleChange(value, pageSize);
    }
  };

  useEffect(() => {
    if (Number(qsParamsPage) !== page) {
      setPage(1);
    }
    if (!defaultPageSize && qsParamsTake === undefined) {
      setPageSize(DEFAULT_PAGE_SIZE);
    }
    if (defaultPageSize) {
      setPageSize(defaultPageSize);
    }
  }, [handleChange, page, defaultPageSize, qsParamsPage, qsParamsTake]);

  return (
    <AmiliPaginationWrapper>
      {!isMobile && (
        <div>
          <CustomTotal>{t('design.total', { QUANTITY: total })}</CustomTotal>
          {subTotalValue && (
            <CustomTotal>
              {t('design.inventorySummary', { liveStock: subTotalValue })}
            </CustomTotal>
          )}
        </div>
      )}

      <AmiliPagesWrapper className="pages-wrapper">
        <AmiliArrowButton
          // UPDATED: More robust disabled check
          className={classNames({ disabled: rangePage.from <= 1 })}
          onClick={handlePrev}
        >
          <Icon name="chevronLeft" width={9} height={12} color="var(--link)" />
        </AmiliArrowButton>

        {/* UPDATED: Dynamically create the array of pages based on the calculated range */}
        {Array.from({ length: rangePage.to - rangePage.from + 1 }).map(
          (_, index) => {
            const value = index + rangePage.from;
            return (
              <AmiliPageWrapper
                key={value} // Use the page number as the key for stability
                onClick={() => {
                  setPage(value);
                  handleChange(value, pageSize);
                }}
                className={classNames({ active: page === value })}
              >
                {value}
              </AmiliPageWrapper>
            );
          },
        )}

        {total === 0 && <AmiliPageWrapper>0</AmiliPageWrapper>}

        <AmiliArrowButton
          className={classNames({ disabled: rangePage.to >= pages })}
          onClick={handleNext}
        >
          <Icon name="chevronRight" width={9} height={12} color="var(--link)" />
        </AmiliArrowButton>
      </AmiliPagesWrapper>

      {!isMobile && showSizeChanger && (
        <SizeChangerWrapper>
          <CustomSelect
            type="linkage"
            value={pageSize}
            options={selectOptions}
            defaultValue={pageSize}
            popupClassName="pagination-popup"
            onChange={value => {
              const newPage = Math.min(Math.ceil(total / Number(value)), page);
              handleChange(newPage, Number(value));
            }}
          />
        </SizeChangerWrapper>
      )}
    </AmiliPaginationWrapper>
  );
};

export default AmiliPagination;
